import { cx } from '@7f-ui'

const STAR_NUMBERS = [1, 2, 3, 4, 5]

// Square boxes on whole pixels: the Figma star (14 × 13.5) sits centred with a little air
// above and below, so the points are not clipped on fractional positions (Retina).
const sizes = {
  base: { gap: 'gap-[2px]', star: 'size-3' },
  md: { gap: 'gap-[5px]', star: 'size-4.5' },
}

type RatingStarsProps = {
  rating: number
  size?: keyof typeof sizes
  className?: string
}

/** Five sand stars, filled in ocker up to the exact rating (e.g. 4,3 → four and a third). */
export function RatingStars({ rating, size = 'base', className }: RatingStarsProps) {
  const { gap, star } = sizes[size]

  return (
    <span className={cx('inline-flex items-center', gap, className)} aria-hidden>
      {STAR_NUMBERS.map(starNumber => {
        const fill = Math.min(Math.max(rating - starNumber + 1, 0), 1) * 100
        return (
          <span
            key={starNumber}
            className={cx('relative inline-block shrink-0', star)}
            data-star-fill={fill > 0 && fill < 100 ? Math.round(fill) : undefined}
          >
            <RatingStar className={cx('absolute inset-0 text-sand', star)} />
            {fill > 0 && (
              <span
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${fill}%` }}
              >
                <RatingStar className={cx('max-w-none text-ocker', star)} />
              </span>
            )}
          </span>
        )
      })}
    </span>
  )
}

// Star symbol exported from the Figma rating components (viewBox 14 × 13.5)
function RatingStar({ className }: { className?: string }) {
  return (
    <svg
      className={cx('block overflow-visible', className)}
      viewBox="0 0 14 13.5"
      fill="none"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M13.6406 6.17188L10.8281 8.60938L11.6875 12.2344C11.6979 12.2656 11.7057 12.3021 11.7109 12.3438C11.7161 12.3854 11.7188 12.4271 11.7188 12.4688C11.7188 12.75 11.6172 12.9922 11.4141 13.1953C11.2109 13.3984 10.9688 13.5 10.6875 13.5C10.5938 13.5 10.5 13.487 10.4062 13.4609C10.3125 13.4349 10.2292 13.3958 10.1562 13.3438V13.3438L7 11.4062L3.84375 13.3438C3.77083 13.3958 3.6875 13.4349 3.59375 13.4609C3.5 13.487 3.40625 13.5 3.3125 13.5C3.03125 13.5 2.78906 13.3984 2.58594 13.1953C2.38281 12.9922 2.28125 12.75 2.28125 12.4688C2.28125 12.4271 2.28385 12.3854 2.28906 12.3438C2.29427 12.3021 2.30208 12.2656 2.3125 12.2344V12.2344L3.17188 8.60938L0.359375 6.17188C0.255208 6.07812 0.169271 5.96354 0.101562 5.82812C0.0338542 5.69271 0 5.55208 0 5.40625C0 5.13542 0.0911458 4.90104 0.273438 4.70312C0.455729 4.50521 0.677083 4.39583 0.9375 4.375V4.375L4.625 4.07812L6.04688 0.640625C6.13021 0.453125 6.25781 0.299479 6.42969 0.179688C6.60156 0.0598958 6.79167 0 7 0C7.20833 0 7.39844 0.0598958 7.57031 0.179688C7.74219 0.299479 7.86458 0.447917 7.9375 0.625V0.640625L9.35938 4.07812L13.0469 4.375C13.3177 4.39583 13.5443 4.50521 13.7266 4.70312C13.9089 4.90104 14 5.13542 14 5.40625C14 5.5625 13.9661 5.70573 13.8984 5.83594C13.8307 5.96615 13.7448 6.07812 13.6406 6.17188"
      />
    </svg>
  )
}
